import { Injectable, computed, signal } from '@angular/core';
import { HdChartMatchScope } from '../bodygraph/chart-tabs/hd-chart-global-settings';

/**
 * Context van de actieve chart-pagina voor de zoektochten die buiten de
 * vaste panelen starten (poort-info, Locks & Keys; spec
 * 2026-10-21-poortactivatie-zoektochten §4.2). De chart-pagina registreert dit
 * en wist het bij vernietiging, zoals `modeLauncher` in de Locks &
 * Keys-staat en `printHandler` in de settings-store: zonder chart-pagina
 * (cheat sheets, gedeelde chart) blijft `chartOpener` leeg en verbergen de
 * zoektochten zich, want er is geen plek om een treffer te openen.
 */
@Injectable({ providedIn: 'root' })
export class HdChartSearchContextService {
  /** Opent een bibliotheek-chart in een nieuwe tab; null = geen chart-pagina actief. */
  readonly chartOpener = signal<((chartId: string) => void) | null>(null);

  /** Library-id van de chart in de actieve tab; null = (nog) niet opgeslagen. */
  readonly activeChartId = signal<string | null>(null);

  /**
   * Identiteit van de actieve chart voor zoekstaat per chart: de library-id, of bij een
   * niet-opgeslagen chart de tab. Wisselt bij elke chartwissel; zoeksecties klappen dan
   * dicht (of tonen de bewaarde staat van die chart) i.p.v. meteen opnieuw te zoeken.
   */
  readonly activeChartKey = signal<string | null>(null);

  /** Bron van de relatie-scope: bij een cycle-tab de natal chart, anders de actieve chart. */
  readonly relationSourceChartId = signal<string | null>(null);

  /** De actieve tab is een composite: "mijn chart" is dan dubbelzinnig (geen L&K-zoektocht). */
  readonly compositeActive = signal(false);

  /** De gebruikerssetting: hele bibliotheek of enkel charts met een relatie tot de bronchart. */
  readonly matchScope = signal<HdChartMatchScope>('library');

  /**
   * Wijzigt die setting (snelkoppeling boven de resultaten van elke zoektocht);
   * de chart-pagina schrijft ze in de settings-store. Null = geen chart-pagina:
   * de toggle verbergt zich dan.
   */
  readonly matchScopeSetter = signal<((scope: HdChartMatchScope) => void) | null>(null);

  /** De actieve chart en zijn relatiebron horen nooit in de treffers. */
  readonly excludeChartIds = computed<readonly string[]>(() => [
    ...new Set([this.activeChartId(), this.relationSourceChartId()].filter((id): id is string => id != null)),
  ]);
}
